<template>
  <div 
    ref="mwe-texteditor" 
    class="mwe-texteditor" 
    :style="{ 
      'visibility': texteditor.show ? 'visible' : 'hidden',
      'min-width': texteditor.container.minWidth,
      'width': texteditor.container.width, 
      'height': texteditor.container.height,
      ...texteditor.container.position, 
    }"
  >
    <div class="mwe-texteditor-container">
      <quill-editor
        ref="editor"
        class="mwe-texteditor-editor editor ql-editing"
        :class="{ 
          [texteditor.styles.join(' ')]: Boolean(texteditor.styles.length > 0),
          [texteditor.style.name]: Boolean(texteditor.style.name),
          ...texteditor.wrapperClass,
        }"
        :style="{ 
          ...(Boolean(texteditor.style.name) ? texteditor.style.cssVars : {}),
        }"
        placeholder="'test'"
        :content="texteditor.container.content"
        :options="editorOptions"
        @focus="onEditorFocus"
        @blur="onEditorBlur"
        @change="onEditorChange"
      />
      <Toolbar
        v-show="texteditor.toolbar.enable && texteditor.toolbar.show"
        ref="toolbar"
        class="mwe-texteditor-toolbar"
        @close="closeToolbar"
      />
      <ToolbarIcon
        v-show="texteditor.selectionIcon.toolbar.show"
        ref="iconToolbar"
        class="mwe-texteditor-iconToolbar"
        @close="unhandledIcon"
      />
      <div
        v-show="texteditor.selectionIcon.frame.show"
        ref="iconFrame"
        class="mwe-texteditor-iconFrame"
        :style="{
          width: `${texteditor.selectionIcon.frame.width}px`,
          height: `${texteditor.selectionIcon.frame.height}px`,
          ...texteditor.selectionIcon.frame.position,
        }"
      >
        <div class="mwe-texteditor-iconFrame-label" :class="texteditor.selectionIcon.frame.labelClass">Icon</div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import { quillEditor } from "vue-quill-editor";
import _ from 'lodash';
import Toolbar from "./Tools/Toolbar";
import ToolbarIcon from "./Tools/ToolbarIcon.vue";

import Quill from "quill";
import ImageIcon from "./Formats/ImageIcon";
import ListItem from "./Formats/ListItem";
import Svg from "./Formats/Svg";
import SvgOld from "./Formats/SvgOld";
import Clipboard from "./Modules/Clipboard";
import FontAwesome from "./Formats/FontAwesome";
import LineBreak from "./Blots/LineBreak";

const Parchment = Quill.import("parchment");
const Template = new Parchment.Attributor.Class('template', 'ql-template', { scope: Parchment.Scope.BLOCK, whitelist: null });
const Font = new Parchment.Attributor.Class('font', 'ql-font', { scope: Parchment.Scope.BLOCK, whitelist: null });
const Align = new Parchment.Attributor.Class('align', 'ql-align', { scope: Parchment.Scope.BLOCK, whitelist: ["right", "left", "center", "justify"] });
const LineFontSize = new Parchment.Attributor.Attribute("lineSize", "data-ql-line-size", { scope: Parchment.Scope.BLOCK, whitelist: null });
const LineHeight = new Parchment.Attributor.Style("lineHeight", "line-height", { scope: Parchment.Scope.BLOCK, whitelist: null });
const LineColor = new Parchment.Attributor.Style("lineColor", "color", { scope: Parchment.Scope.BLOCK, whitelist: null });
const LineFontFamily = new Parchment.Attributor.Style("lineFontFamily", "font-family", { scope: Parchment.Scope.BLOCK, whitelist: null });
const ListItemColor = new Parchment.Attributor.Attribute("listItemColor", "data-ql-list-item-color", { scope: Parchment.Scope.BLOCK, whitelist: null });
const ListItemFontFamily = new Parchment.Attributor.Attribute("listItemFontFamily", "data-ql-list-item-font-family", { scope: Parchment.Scope.BLOCK, whitelist: null });
const FontFamily = new Parchment.Attributor.Style("family", "font-family", { scope: Parchment.Scope.INLINE, whitelist: null });
const FontSize = new Parchment.Attributor.Style("size", "font-size", { scope: Parchment.Scope.INLINE, whitelist: null });
const FontSizeAttribute = new Parchment.Attributor.Attribute("sizeAttribute", "data-ql-size", { scope: Parchment.Scope.INLINE, whitelist: null });
const FontWeight = new Parchment.Attributor.Style("fontWeight", "font-weight", { scope: Parchment.Scope.INLINE, whitelist: ['bold', 'normal'] });
const FontStyle = new Parchment.Attributor.Style("fontStyle", "font-style", { scope: Parchment.Scope.INLINE, whitelist: ['italic', 'normal'] });
const Decoration = new Parchment.Attributor.Style("decoration", "text-decoration", { scope: Parchment.Scope.INLINE, whitelist: ['underline', 'none'] });
const Color = new Parchment.Attributor.Style("color", "color", { scope: Parchment.Scope.INLINE, whitelist: null });
const Background = new Parchment.Attributor.Style("background", "background-color", { scope: Parchment.Scope.INLINE, whitelist: null });
const LinkType = new Parchment.Attributor.Attribute("linkType", "data-ql-link-type", { scope: Parchment.Scope.INLINE, whitelist: null });
const LinkPageID = new Parchment.Attributor.Attribute("linkPageID", "data-ql-link-pageid", { scope: Parchment.Scope.INLINE, whitelist: null });
const LinkSectionID = new Parchment.Attributor.Attribute("linkSectionID", "data-ql-link-sectionid", { scope: Parchment.Scope.INLINE, whitelist: null });
const LinkPhoneCode = new Parchment.Attributor.Attribute("linkPhoneCode", "data-ql-link-phonecode", { scope: Parchment.Scope.INLINE, whitelist: null });
const LinkFileID = new Parchment.Attributor.Attribute("linkFileID", "data-ql-link-fileid", { scope: Parchment.Scope.INLINE, whitelist: null });
const LinkFileName = new Parchment.Attributor.Attribute("linkFileName", "data-ql-link-filename", { scope: Parchment.Scope.INLINE, whitelist: null });
const LinkTarget = new Parchment.Attributor.Attribute("linkTarget", "target", { scope: Parchment.Scope.INLINE, whitelist: null });
const FontOld = new Parchment.Attributor.Class('fontOld', 'ql-font', { scope: Parchment.Scope.INLINE, whitelist: null }); // will delete
const LinkTypeOld = new Parchment.Attributor.Attribute("linkTypeOld", "linktype", { scope: Parchment.Scope.INLINE, whitelist: null }); // will delete
const LinkPageIdOld = new Parchment.Attributor.Attribute("linkPageIdOld", "linkpageid", { scope: Parchment.Scope.INLINE, whitelist: null }); // will delete
const LinkSectionIdOld = new Parchment.Attributor.Attribute("linkSectionIdOld", "linksectionid", { scope: Parchment.Scope.INLINE, whitelist: null }); // will delete
const LinkDocNameOld = new Parchment.Attributor.Attribute("linkDocNameOld", "linkdocname", { scope: Parchment.Scope.INLINE, whitelist: null }); // will delete
const PhoneCodeOld = new Parchment.Attributor.Attribute("phoneCodeOld", "phonecode", { scope: Parchment.Scope.INLINE, whitelist: null }); // will delete

Quill.register(Template, true);
Quill.register(Font, true);
Quill.register(Align, true);
Quill.register(LineFontSize, true);
Quill.register(LineHeight, true);
Quill.register(LineColor, true);
Quill.register(LineFontFamily, true);
Quill.register(ListItemColor, true);
Quill.register(ListItemFontFamily, true);
Quill.register(FontFamily, true);
Quill.register(FontSize, true);
Quill.register(FontSizeAttribute, true); // for responsive
Quill.register(FontWeight, true);
Quill.register(FontStyle, true);
Quill.register(Decoration, true);
Quill.register(Color, true);
Quill.register(Background, true);
Quill.register(LinkType, true);
Quill.register(LinkPageID, true);
Quill.register(LinkSectionID, true);
Quill.register(LinkPhoneCode, true);
Quill.register(LinkFileID, true);
Quill.register(LinkFileName, true);
Quill.register(LinkTarget, true);
Quill.register(FontOld, true); // will delete
Quill.register(LinkTypeOld, true); // will delete
Quill.register(LinkPageIdOld, true); // will delete
Quill.register(LinkSectionIdOld, true); // will delete
Quill.register(LinkDocNameOld, true); // will delete
Quill.register(PhoneCodeOld, true); // will delete
Quill.register(ImageIcon, true);
Quill.register(ListItem, true);
Quill.register(Svg, true);
Quill.register(SvgOld, true);
Quill.register("modules/clipboard", Clipboard, true);
Quill.register(FontAwesome, true);
Quill.register("blots/embed/line-break", LineBreak, true);
// console.log('=== Quill.vue: Quill.imports ===', Quill.imports);

export default {
  components: {
    quillEditor,
    Toolbar,
    ToolbarIcon,
  },
  computed: {
    ...mapGetters({
      preview: "designIframe/preview",
      detectDevice: "detectDevice",
      screenSize: "screenSize",
      texteditor: "designIframe/texteditor/texteditor",
      templateFonts: "designIframe/metadata/templateFonts",
      templateFontsLanguage: "designIframe/metadata/templateFontsLanguage",
      templateColor: "designIframe/metadata/templateColor",
      siteComponents: "designIframe/metadata/siteComponents",
      components: "designIframe/metadata/components",
      widgetActive: "designIframe/frame/widgetActive",
    }),
    themeFonts() {
      return this.templateFonts || {};
    },
    themeFontsLanguage() {
      return this.templateFontsLanguage || {};
    },
    templateFormats() {
      let templateFormats = {};
      Object.keys(this.themeFonts).forEach((font) => {
        const themeFont = {
          ...this.themeFonts[font],
          ...this.themeFontsLanguage[font],
        }
        templateFormats[font] = {
          family: this.normalizeFormatFamily(themeFont.family),
          size: this.normalizeFormatSize(themeFont.size),
          fontWeight: this.normalizeFormatFontWeight(themeFont.isBold),
          fontStyle: this.normalizeFormatFontStyle(themeFont.isItalic),
          // decoration: this.normalizeFormatDecoration(themeFont.isUnderline),
          color: this.normalizeFormatColor(themeFont.color),
          lineHeight: 1.2,
        }
      });
      return templateFormats;
    },
    component() {
      return this.components[this.texteditor.componentID]
        ? this.components[this.texteditor.componentID]
        : this.siteComponents[this.texteditor.componentID];
    },
    editor() {
      return this.$refs.editor.quill;
    },
    editorOptions() {
      return {
        theme: 'bubble',
        modules: {
          toolbar: false,
          keyboard: {
            bindings: {
              backspace: {
                key: 'backspace',
                handler:(range, context) => {
                  const firstFormats = this.texteditor.editor.getFormat(range.index);
                  const formats = firstFormats;
                  const formatsLine = firstFormats;
                  
                  // Override handleDeleteRange 
                  if (context.offset === 0) { // when delete all char of line(s)
                    let lines = this.texteditor.editor.getLines(range);
                    let linesLenght = 0;
                    if (lines.length) {
                      lines.forEach((line) => {
                        linesLenght = linesLenght + line.length();
                      })
                    }

                    if (lines.length && (range.length == linesLenght || range.length == (linesLenght - 1))) { // not remian <br> || remian <br>
                      // Delete Range
                      this.texteditor.editor.deleteText(range, Quill.sources.USER);

                      // Keep Formats
                      if (range.length == (linesLenght - 1)) { // remain <br>
                        this.texteditor.editor.formatLine(range.index, 1, formatsLine, Quill.sources.USER);
                        Object.keys(formats).forEach((name) => {
                          this.texteditor.editor.format(name, formats[name], Quill.sources.USER);
                        });
                      }

                      // Set Selection
                      this.texteditor.editor.setSelection(range.index, Quill.sources.SILENT);
                      this.texteditor.editor.focus();

                      return;
                    }
                  }
                  
                  // Override handleBackspace
                  if (context.offset === 1) { //  when delete last char of line (lenght: 1)
                    let [line, ] = this.texteditor.editor.getLine(range.index);
                    let lineLenght = line ? line.length() : 0;

                    if (range.length == 0 && lineLenght == 2) {
                      // Delete Char
                      let length = /[\uD800-\uDBFF][\uDC00-\uDFFF]$/.test(context.prefix) ? 2 : 1;
                      this.texteditor.editor.deleteText(range.index-length, length, Quill.sources.USER);

                      // Keep Formats
                      this.texteditor.editor.formatLine(range.index-length, length, formatsLine, Quill.sources.USER);
                      Object.keys(formats).forEach((name) => {
                        this.texteditor.editor.format(name, formats[name], Quill.sources.USER);
                      });

                      // Set Selection
                      this.texteditor.editor.focus();

                      return;
                    }
                  }
                  
                  return true;
                },
              },
              13: {
                key: 13,
                shiftKey: false,
                handler: (range) => {
                  if (this.texteditor.keyboard.enter === false) return;

                  // Override handleEnter
                  const firstFormats = this.texteditor.editor.getFormat(range.index);
                  const formats = firstFormats;
                  const formatsLine = firstFormats;
                  
                  // Delete Range when selected
                  if (range.length > 0) {
                    this.texteditor.editor.scroll.deleteAt(range.index, range.length);  // So we do not trigger text-change
                  }

                  // Insert <br>
                  this.texteditor.editor.insertText(range.index, '\n', formatsLine, Quill.sources.USER);
                  
                  // Set Selection
                  this.texteditor.editor.setSelection(range.index + 1, Quill.sources.SILENT);
                  this.texteditor.editor.focus();

                  // Keep Formats
                  this.texteditor.editor.formatLine(range.index, 1, formatsLine, Quill.sources.USER);
                  Object.keys(formats).forEach((name) => {
                    this.texteditor.editor.format(name, formats[name], Quill.sources.USER);
                  });

                  return;
                },
              },
              '13+shift': {
                key: 13,
                shiftKey: true,
                handler: (range) => {
                  if (this.texteditor.keyboard.enter === false) return;
                  
                  const firstFormats = this.texteditor.editor.getFormat(range.index);
                  const formats = firstFormats;
                  
                  // Delete Range when selected
                  if (range.length > 0) {
                    this.texteditor.editor.scroll.deleteAt(range.index, range.length);  // So we do not trigger text-change
                  }

                  // Insert <br>
                  this.texteditor.editor.insertEmbed(range.index, 'line-break', true, Quill.sources.USER);

                  // Keep Formats
                  this.texteditor.editor.formatText({ index: range.index, length: 1 }, { ...formats });
                  
                  // Set Selection
                  this.texteditor.editor.setSelection(range.index + 1, Quill.sources.SILENT);
                  this.texteditor.editor.focus();

                  return;
                },
              },
              37: { // ArrowLeft
                key: 37,
                shiftKey: null,
                handler: () => {
                  let elFontAwesome = this.editor.root.getElementsByClassName("font-awesome")
                  if (elFontAwesome.length > 0) {
                    elFontAwesome.forEach(el => {
                      el.classList.remove('active')
                      this.editor.root.style.caretColor = null
                    });
                  }
                  return true;
                },
              },
              38: { // ArrowUp
                key: 38,
                shiftKey: null,
                handler: () => {
                  let elFontAwesome = this.editor.root.getElementsByClassName("font-awesome")
                  if (elFontAwesome.length > 0) {
                    elFontAwesome.forEach(el => {
                      el.classList.remove('active')
                      this.editor.root.style.caretColor = null
                    });
                  }
                  return true;
                },
              },
              39: { // ArrowRight
                key: 39,
                shiftKey: null,
                handler: () => {
                  let elFontAwesome = this.editor.root.getElementsByClassName("font-awesome")
                  if (elFontAwesome.length > 0) {
                    elFontAwesome.forEach(el => {
                      el.classList.remove('active')
                      this.editor.root.style.caretColor = null
                    });
                  }
                  return true;
                },
              },
              40: { // ArrowDown
                key: 40,
                shiftKey: null,
                handler: () => {
                  let elFontAwesome = this.editor.root.getElementsByClassName("font-awesome")
                  if (elFontAwesome.length > 0) {
                    elFontAwesome.forEach(el => {
                      el.classList.remove('active')
                      this.editor.root.style.caretColor = null
                    });
                  }
                  return true;
                },
              },
            },
          },
        },
      }
    },
  },
  methods: {
    // Quill
    updateQuillFormats(formats) {
      let Block = Quill.import("blots/block");
      let Inline = Quill.import("blots/inline");
      // let Font = Quill.import("formats/font");
      // let FontOld = Quill.import("formats/fontOld");
      // let FontFamily = Quill.import("formats/family");
      // let FontSize = Quill.import("formats/size");
      // let FontSizeAttribute = Quill.import("formats/sizeAttribute");
      // let LineHeight = Quill.import("formats/lineHeight");
      // let FontWeight = Quill.import("formats/fontWeight");
      // let FontStyle = Quill.import("formats/fontStyle");
      // let Decoration = Quill.import("formats/decoration");
      // let Align = Quill.import("formats/align");
      // let LinkType = Quill.import("formats/linkType");
      // let LinkPageID = Quill.import("formats/linkPageID");
      // let LinkSectionID = Quill.import("formats/linkSectionID");
      // let LinkPhoneCode = Quill.import("formats/linkPhoneCode");
      // let LinkFileID = Quill.import("formats/linkFileID"); 
      // let LinkFileName = Quill.import("formats/linkFileName"); 
      // let LinkTarget = Quill.import("formats/linkTarget");
      // let LinkTypeOld = Quill.import("formats/linkTypeOld"); // will delete
      // let LinkPageIdOld = Quill.import("formats/linkPageIdOld"); // will delete
      // let LinkSectionIdOld = Quill.import("formats/linkSectionIdOld"); // will delete
      // let LinkDocNameOld = Quill.import("formats/linkDocNameOld"); // will delete
      // let PhoneCodeOld = Quill.import("formats/phoneCodeOld"); // will delete
      // // let IsIcon = Quill.import("formats/isIcon");
      // let IconContent = Quill.import("formats/iconContent");
      // let IconName = Quill.import("formats/iconName");
      // let IconSize = Quill.import("formats/iconSize");
      // let IconColor = Quill.import("formats/iconColor");
      // // let IconIndex = Quill.import("formats/iconIndex");

      if (!_.isEqual(Block.tagName, formats.block.tagName)) { 
        Block.tagName = formats.block.tagName; Quill.register(Block, true); 
        Inline.tagName = 'SPAN'; Quill.register(Inline, true);
      }
      // if (!_.isEqual(Font.whitelist, formats.font.whitelist)) { Font.whitelist = formats.font.whitelist; Quill.register(Font, true); }
      // if (!_.isEqual(FontOld.whitelist, formats.fontOld.whitelist)) { FontOld.whitelist = formats.fontOld.whitelist; Quill.register(FontOld, true); }
      // if (!_.isEqual(FontFamily.whitelist, formats.family.whitelist)) { FontFamily.whitelist = formats.family.whitelist; Quill.register(FontFamily, true); }
      // if (!_.isEqual(FontSize.whitelist, formats.size.whitelist)) { FontSize.whitelist = formats.size.whitelist; Quill.register(FontSize, true); }
      // if (!_.isEqual(FontSizeAttribute.whitelist, formats.sizeAttribute.whitelist)) { FontSizeAttribute.whitelist = formats.sizeAttribute.whitelist; Quill.register(FontSizeAttribute, true); }
      // if (!_.isEqual(LineHeight.whitelist, formats.lineHeight.whitelist)) { LineHeight.whitelist = formats.lineHeight.whitelist; Quill.register(LineHeight, true); }
      // if (!_.isEqual(FontWeight.whitelist, formats.fontWeight.whitelist)) { FontWeight.whitelist = formats.fontWeight.whitelist; Quill.register(FontWeight, true); }
      // if (!_.isEqual(FontStyle.whitelist, formats.fontStyle.whitelist)) { FontStyle.whitelist = formats.fontStyle.whitelist; Quill.register(FontStyle, true); }
      // if (!_.isEqual(Decoration.whitelist, formats.decoration.whitelist)) { Decoration.whitelist = formats.decoration.whitelist; Quill.register(Decoration, true); }
      // if (!_.isEqual(Align.whitelist, formats.align.whitelist)) { Align.whitelist = formats.align.whitelist; Quill.register(Align, true); }
      // if (!_.isEqual(LinkType.whitelist, formats.linkType.whitelist)) { LinkType.whitelist = formats.linkType.whitelist; Quill.register(LinkType, true); }
      // if (!_.isEqual(LinkPageID.whitelist, formats.linkPageID.whitelist)) { LinkPageID.whitelist = formats.linkPageID.whitelist; Quill.register(LinkPageID, true); }
      // if (!_.isEqual(LinkSectionID.whitelist, formats.linkSectionID.whitelist)) { LinkSectionID.whitelist = formats.linkSectionID.whitelist; Quill.register(LinkSectionID, true); }
      // if (!_.isEqual(LinkPhoneCode.whitelist, formats.linkPhoneCode.whitelist)) { LinkPhoneCode.whitelist = formats.linkPhoneCode.whitelist; Quill.register(LinkPhoneCode, true); }
      // if (!_.isEqual(LinkFileID.whitelist, formats.linkFileID.whitelist)) { LinkFileID.whitelist = formats.linkFileID.whitelist; Quill.register(LinkFileID, true); }
      // if (!_.isEqual(LinkFileName.whitelist, formats.linkFileName.whitelist)) { LinkFileName.whitelist = formats.linkFileName.whitelist; Quill.register(LinkFileName, true); }
      // if (!_.isEqual(LinkTarget.whitelist, formats.linkTarget.whitelist)) { LinkTarget.whitelist = formats.linkTarget.whitelist; Quill.register(LinkTarget, true); }
      // if (!_.isEqual(LinkTypeOld.whitelist, formats.linkTypeOld.whitelist)) { LinkTypeOld.whitelist = formats.linkTypeOld.whitelist; Quill.register(LinkTypeOld, true); }
      // if (!_.isEqual(LinkPageIdOld.whitelist, formats.linkPageIdOld.whitelist)) { LinkPageIdOld.whitelist = formats.linkPageIdOld.whitelist; Quill.register(LinkPageIdOld, true); }
      // if (!_.isEqual(LinkSectionIdOld.whitelist, formats.linkSectionIdOld.whitelist)) { LinkSectionIdOld.whitelist = formats.linkSectionIdOld.whitelist; Quill.register(LinkSectionIdOld, true); }
      // if (!_.isEqual(LinkDocNameOld.whitelist, formats.linkDocNameOld.whitelist)) { LinkDocNameOld.whitelist = formats.linkDocNameOld.whitelist; Quill.register(LinkDocNameOld, true); }
      // if (!_.isEqual(PhoneCodeOld.whitelist, formats.phoneCodeOld.whitelist)) { PhoneCodeOld.whitelist = formats.phoneCodeOld.whitelist; Quill.register(PhoneCodeOld, true); }
      
      // // if (!_.isEqual(IsIcon.whitelist, formats.isIcon.whitelist)) { IsIcon.whitelist = formats.isIcon.whitelist; Quill.register(IsIcon, true); }
      // if (!_.isEqual(IconContent.whitelist, formats.iconContent.whitelist)) { IconContent.whitelist = formats.iconContent.whitelist; Quill.register(IconContent, true); }
      // if (!_.isEqual(IconName.whitelist, formats.iconName.whitelist)) { IconName.whitelist = formats.iconName.whitelist; Quill.register(IconName, true); }
      // if (!_.isEqual(IconSize.whitelist, formats.iconSize.whitelist)) { IconSize.whitelist = formats.iconSize.whitelist; Quill.register(IconSize, true); }
      // if (!_.isEqual(IconColor.whitelist, formats.iconColor.whitelist)) { IconColor.whitelist = formats.iconColor.whitelist; Quill.register(IconColor, true); }
      // // if (!_.isEqual(IconIndex.whitelist, formats.iconIndex.whitelist)) { IconIndex.whitelist = formats.iconIndex.whitelist; Quill.register(IconIndex, true); }
    },

    // Toolbar
    openToolbar() {
      this.$store.dispatch("designIframe/texteditor/showToolbar");
    },
    closeToolbar() {
      this.$store.dispatch("designIframe/texteditor/hideToolbar");
    },
    reToolbar() {
      if (!this.texteditor.init) return;
      const $window = window;
      const $container = this.texteditor.container.target;
      const $toolbar = this.texteditor.toolbar.target;
      const rectContainer = $container.getBoundingClientRect();
      const rectToolbar = $toolbar.getBoundingClientRect();

      const win_t = 0;
      const win_b = $window.innerHeight;
      const win_l = 0;
      const win_r = $window.innerWidth;

      const container_w = rectContainer.width;
      const container_h = rectContainer.height;
      const container_t = rectContainer.top;
      const container_b = rectContainer.bottom;
      const container_l = rectContainer.left;
      const container_r = rectContainer.right;

      const tool_w = rectToolbar.width;
      const tool_h = rectToolbar.height;

      const space_l = container_l;
      const space_r = win_r - container_r;
      const space_t = container_t;
      const space_b = win_b - container_b;

      const gap_x = 16;
      const gap_y = 16;

      let position = {};

      const normalizeTop = (value) => {
        const topMin = (win_t + gap_y) - container_t;
        const topMax = (win_b - gap_y - tool_h) - container_t;
        return Math.min(Math.max(value, topMin), topMax);
      }
      const normalizeLeft = (value) => {
        const leftMin = (win_l + gap_x) - container_l;
        const leftMax = (win_r - gap_x - tool_w) - container_l;
        return Math.min(Math.max(value, leftMin), leftMax);
      }

      if (space_r > tool_w + gap_x) {
        let left = 0 + container_w + gap_x; // right outer
        let top = normalizeTop(0); // top inner
        position.top = `${top}px`;
        position.left = `${left}px`;

      } else if (space_l > tool_w + gap_x) {
        let left = 0 - gap_x - tool_w; // left outer
        let top = normalizeTop(0); // top inner
        position.top = `${top}px`;
        position.left = `${left}px`;

      // } else if (space_t > tool_h + gap_y) {
      //   let top = 0 - gap_y - tool_h; // top outer
      //   let left = normalizeLeft(container_w - tool_w); // right inner
      //   position.top = `${top}px`;
      //   position.left = `${left}px`;

      // } else if (space_b > tool_h + gap_y) {
      //   let top = 0 + gap_y + container_h; // bottom outer
      //   let left = normalizeLeft(container_w - tool_w); // right inner
      //   position.top = `${top}px`;
      //   position.left = `${left}px`;

      } else {
        if (space_r >= space_l) {
          let left = normalizeLeft(0 + container_w + gap_x); // right outer
          position.left = `${left}px`;
        } else {
          let left = normalizeLeft(0 - gap_x - tool_w); // left outer
          position.left = `${left}px`;
        }
        
        if (space_t >= space_b) {
          let top = normalizeTop(0 - gap_y - tool_h); // top outer
          position.top = `${top}px`;
        } else {
          let top = normalizeTop(0 + gap_y + container_h); // bottom outer
          position.top = `${top}px`;
        }
      }

      this.$store.dispatch('designIframe/texteditor/setToolbar', { position: position });
    },

    // ToolIcon
    async handledIcon(iconBlot) {
      if (!this.texteditor.toolbar.tools.includes('icon')) return;
      const node = iconBlot.domNode;

      // Target
      await this.$store.dispatch("designIframe/texteditor/setSelectionIcon", { target: node }); // setup icon target

      // Focus
      this.texteditor.editor.setSelection(iconBlot.offset(this.texteditor.editor.scroll), 1, 'user');

      // Class
      if (node.closest('.ql-editor').querySelector('.icon.active')) {
        node.closest('.ql-editor').querySelector('.icon.active').classList.remove("active");
      }
      node.classList.add("active");
      
      // Toolbar
      // await this.$store.dispatch("designIframe/texteditor/showIconFrame");
      this.$store.dispatch("designIframe/texteditor/showIconToolbar");
      this.$store.dispatch("designIframe/texteditor/hideToolbar");

      // Callback
      this.$store.dispatch("designIframe/texteditor/onFocusIcon");
    },
    async unhandledIcon() {
      if (!this.texteditor.toolbar.tools.includes('icon')) return;
      const node = this.texteditor.selectionIcon.target;
      
      if(!node) return;

      // Target
      await this.$store.dispatch("designIframe/texteditor/setSelectionIcon", { target: undefined }); // setup icon target

      // Class
      node.classList.remove("active");

      // Toolbar
      // this.$store.dispatch("designIframe/texteditor/hideIconFrame");
      this.$store.dispatch("designIframe/texteditor/hideIconToolbar");
      this.$store.dispatch("designIframe/texteditor/showToolbar");

      // Callback
      this.$store.dispatch("designIframe/texteditor/onBlurIcon");
    },

    // ThemeFont to Templates
    normalizeFormatFamily(value) {
      if (!value) return undefined;
      if (!value.startsWith('"')) return `"${value}"`;
      return value;
    },
    normalizeFormatSize(value) {
      if (!value) return undefined;
      value = parseInt(value);
      return `${value}px`;
    },
    normalizeFormatFontWeight(value) {
      if (!value) return undefined;
      return 'bold';
    },
    normalizeFormatFontStyle(value) {
      if (!value) return undefined;
      return 'italic';
    },
    normalizeFormatDecoration(value) {
      if (!value) return undefined;
      return 'underline';
    },
    normalizeFormatColor(value) {
      if (!value) return undefined;
      if (value.startsWith('--tpc-')) return `rgba(var(${value}), 100%)`; // --tpc-11 => rgba(var(--tpc-11), 100%)
      return value.toUpperCase(); // #xxxxxx => #XXXXXX
    },
    
    // Events
    async detectLeaveTextEditor(event) {
      if (this.texteditor.active) {
        const IconBlot = Quill.import('formats/icon');
        const Parchment = Quill.import('parchment');

        if (event.target) {
          let leaf = Parchment.find(event.target) || Parchment.find(event.target.closest('span.icon'));
          if (leaf) {
            if (leaf instanceof IconBlot) {
              this.handledIcon(leaf);
            } else {
              this.unhandledIcon();
            }
          }
        }

        let $texteditor = this.$refs["mwe-texteditor"];
        let $original = this.texteditor.original.target;
        let forceFocus = this.texteditor.forceFocus;

        if (
          !$texteditor.contains(event.target) && 
          !$original.contains(event.target) && 
          !forceFocus
        ) {
          this.unhandledIcon();
          this.$store.dispatch("designIframe/texteditor/onBlur", { event });
        }
      }
    },
    async onEditorFocus(event) {
      if (this.texteditor.show) {
        if (!this.texteditor.toolbar.show && !this.texteditor.selectionIcon.toolbar.show) this.openToolbar();
        this.$store.dispatch("designIframe/texteditor/onFocus", { event });
      }
    },
    async onEditorChange(event) {
      if (this.texteditor.active) {
        if (this.texteditor.show) {
          this.$store.dispatch("designIframe/texteditor/onChange", { event });
        } else { // from setupContent(), clearContent()
          this.$store.dispatch("designIframe/texteditor/setContent", { content: event.html });
        }
      }
    },
    onEditorBlur() {
      if (this.texteditor.colorpicker) return;
      if (this.texteditor.backgroundpicker) return;
      if (this.texteditor.show) {
        this.texteditor.editor.setSelection(this.texteditor.editor.selection.savedRange); // continue focus
      }
    },
    onEditorKeyDown() {
      this.unhandledIcon();
      // const IconBlot = Quill.import('formats/icon');
      // // const TextBlot = Quill.import('blots/text');

      // if (e.key == 'ArrowRight') {
      //   const selection = this.texteditor.editor.getSelection();
      //   const [leaf, leafIndex] = this.texteditor.editor.getLeaf(selection.index);

      //   if (leaf instanceof IconBlot) {
      //     const iconCurr = this.texteditor.selectionIcon.target;
      //     if (!iconCurr) {
      //       this.handledIcon(leaf);
      //     } else {
      //       this.unhandledIcon();
      //       if (selection.length == 0) {
      //         this.texteditor.editor.setSelection(selection.index - 1, 0);
      //       }
      //       return;
      //     }
      //   }
      // }
    },
    setOnClickFontAwesome() {
      this.editor.root.addEventListener('click', (ev) => {
        let blot = Parchment.find(ev.target);
        this.editor.root.style.caretColor = null
        let elFontAwesome = this.editor.root.getElementsByClassName("font-awesome")
        if (elFontAwesome.length > 0) {
          elFontAwesome.forEach(el => {
            el.classList.remove('active')
            this.editor.root.style.caretColor = null
          });
          if (blot instanceof FontAwesome) {
            ev.target.classList.add('active')
            this.editor.setSelection(blot.offset(this.editor.scroll), 1);
            this.editor.root.style.caretColor = 'transparent'
          }
        }
      });
    },
  },
  mounted() {
    this.$store.commit("ADD_MOUSEDOWN_EVENT", this.detectLeaveTextEditor);
    this.$store.dispatch("designIframe/texteditor/initTextEditor", {
      templateFormats: this.templateFormats,
      editor: this.editor,
      containerTarget: this.$refs['mwe-texteditor'],
      toolbarTarget: this.$refs.toolbar.$el,
      iconToolbarTarget: this.$refs.iconToolbar.$el,
      iconFrameTarget: this.$refs.iconFrame,
    });
    this.editor.root.addEventListener('keydown', this.onEditorKeyDown);
    this.setOnClickFontAwesome()
  },
  beforeDestroy() {
    this.$store.commit("REMOVE_MOUSEDOWN_EVENT", this.detectLeaveTextEditor);
    this.editor.root.removeEventListener('keydown', this.onEditorKeyDown);
  },
  watch: {
    'texteditor.formats': {
      deep: true,
      handler(newValue) {
        this.updateQuillFormats(newValue);
      }
    },
    'templateFormats': {
      deep: true,
      async handler(newValue) {
        await this.$store.dispatch("designIframe/texteditor/setData", { templateFormatsDefault: newValue });
        await this.$store.dispatch("designIframe/texteditor/setupTemplateFormats", {});
      }
    },
    'texteditor.toolbar.show': {
      deep: true,
      handler() {
        this.$nextTick(function () {
          this.reToolbar();
        });
      }
    },
  }
};
</script>

<style lang="scss">
// <style lang="scss" scoped>
.mwe-texteditor {
  position: absolute;
  // z-index: 99;
  
  .mwe-texteditor-container {
    position: relative;
    z-index: 200;
  }

  .ql-editor:focus-visible {
    outline: unset;
  }
  .ql-clipboard {
    visibility: hidden;
  }
  .ql-tooltip-arrow {
    display: none !important;
  }
  .ql-tooltip-editor {
    display: none !important;
  }
  
  p {
    margin-block-start: unset;
    margin-block-end: unset;
  }
  .tool {
    z-index: 201;
    position: absolute;
  }

  // Icon
  .quill-editor .icon {
    // background: red;
    position: relative;
    cursor: pointer;
    z-index: 1;
  }
  .quill-editor .icon:hover { z-index: 3; }
  .quill-editor .icon.active { z-index: 2; }
  .quill-editor .icon span {
    // background: blue;
    position: relative;
    display: inline-flex;
    vertical-align: middle;
  }
  .quill-editor .icon.active span {
    outline: solid 3px #12a9ff;
    z-index: 2;
    height: 100%;
  }
  .quill-editor .icon.active span::after {
    content: 'Icon';
    position: absolute;
    width: fit-content;
    padding: 2px 6px;
    font-family: 'Roboto';
    font-size: 12px;
    line-height: 12px;
    color: #FFFFFF;
    background-color: #12A9FF;
    border: 1px solid #12A9FF;
    pointer-events: all;
    width: max-content;
    top: -20px;
    left: -3px;
  }
  .quill-editor .icon:not(.active):hover span {
    outline: solid 1px #ADDFFC;
    z-index: 2;
  }
  // .quill-editor .icon svg {
  //   // background: yellow;
  // }
  // .quill-editor .icon:not(.active):hover svg {
  //   outline: solid 1px #ADDFFC;
  //   z-index: 2;
  // }
  // .quill-editor .icon.active svg {
  //   outline: solid 3px #12a9ff;
  //   z-index: 2;
  // }

  .editor, .ql-editor, .quill-editor {
    white-space: normal!important;
  }
  .editor {
    z-index: 1;
  }
  .one-line .ql-editor {
    white-space: nowrap;
    overflow: hidden !important;
    /* text-overflow: clip; */
  }
  .one-line >>> .ql-editor.ql-blank::before {
    display: none;
  }
  .text-header-layout-1,
  .text-header-layout-3,
  .text-header-layout-5 {
    min-width: 40px;
    min-height: 30px;
    /* margin-top: -5px; */
    /* margin-bottom: 10px; */
    /* outline: 2px solid #12a9ff; */
    /* padding: 0; */
    /* line-height: normal; */
    /* display: grid; */
    /* align-items: center; */
  }
  .text-header-layout-2,
  .text-header-layout-4 {
    min-width: 40px;
    min-height: 30px;
    /* line-height: normal; */
    /* margin-top: -5px; */
    /* outline: 2px solid #12a9ff; */
    /* white-space: nowrap; */
    /* display: grid; */
    /* align-items: center; */
    /* justify-content: flex-end; */
    /* position: absolute; */
    /* left: 0px; */
    /* top: 0px; */
    /* z-index: 1; */
  }

  /* .text-header-layout-2 >>> .ql-editor, .text-header-layout-4 >>> .ql-editor {
    text-align: end;
    padding-right: 5px;
  } */
  /* .text-footer {
    outline: 2px solid #12a9ff;
  } */


  // Custom Style
  .ql-style-section_widget_button {
    * {
      font-family: var(--font-family) !important;
      font-size: var(--font-size) !important;
      color: var(--color) !important;
      font-weight: var(--is-bold) !important;
      text-decoration: var(--is-underline) !important;
      font-style: var(--is-italic) !important;
    }
  }

  // Custom Style
  .ql-style-section_widget_table {
    .ql-template-tablewidget {
      font-family: var(--template-table-editng-font-family) !important;
      font-size: var(--template-table-editng-font-size);
      color: var(--template-table-editng-font-color) !important;
      font-weight: var(--template-table-editng-font-weight) !important;
      text-decoration: var(--template-table-editng-font-decoration) !important;
      font-style: var(--template-table-editng-font-style) !important;
      text-align: var(--template-table-editng-text-align);
    }
    .ql-align-right {
      text-align: right !important;
    }
    .ql-align-left {
      text-align: left !important;
    }
    .ql-align-center {
      text-align: center !important;
    }
    .ql-align-justify {
      text-align: justify !important;
    }
  }

  .ql-editing {
    /* overflow: hidden; */
  }

  // Icon Toolbar
  .mwe-texteditor-iconToolbar {
    z-index: 3;
  }

  // Icon Frame
  .mwe-texteditor-iconFrame {
    position: absolute;
    z-index: 1;
    min-width: calc(8px + 4px + 4px);
    min-height: calc(8px + 4px + 4px);
    border: 4px solid #12a9ff;
  }
  .mwe-texteditor-iconFrame-label {
    position: absolute;
    width: fit-content;
    padding: 2px 6px;
    font-family: 'Roboto';
    font-size: 12px;
    line-height: 12px;
    color: #FFFFFF;
    background-color: #12A9FF;
    border: 1px solid #12A9FF;
    pointer-events: all;
  }
  .mwe-texteditor-iconFrame-label.top {
    bottom: calc(100% + 4px);
  }
  .mwe-texteditor-iconFrame-label.left {
    left: -4px;
  }
  .mwe-texteditor-iconFrame-label.right {
    right: -4px;
  }
  .mwe-texteditor-iconFrame-label.bottom {
    top: calc(100% + 4px);
  }

  .quill-editor .ql-editor {
    .ql-align-left, .ql-align-center, .ql-align-right, .ql-align-justify{
      white-space: pre-wrap;  // For fix spacebar not working on firefox
    }
  } 

  .font-awesome {
    cursor: pointer;
    span {
      pointer-events: none;
    }
  }
  .font-awesome.active span svg {
    outline: 4px solid #12a9ff;
  }
  .svg-font-awesome.active {
    outline: 4px solid #12a9ff;
  }
  .quill-editor {
    sub, sup {
      font-size: smaller !important;
    }
  }
}
</style>